import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Calendar } from "lucide-react";
import api, { formatDate, resolveImageUrl } from "@/lib/api";
import ArticleCard from "@/components/ArticleCard";
import SeoHead from "@/components/SeoHead";
import InlineNewsletter from "@/components/InlineNewsletter";
import ArticleAudioPlayer from "@/components/ArticleAudioPlayer";
import ShareSection from "@/components/ShareSection";
import ReviewSection from "@/components/ReviewSection";
import AuthorSection from "@/components/AuthorSection";
import SmartCoverImage from "@/components/SmartCoverImage";

import {
  BODY_CONTAINER,
  BODY_SCOPED_CSS,
  renderPlainTextArticle,
  sanitizeArticleHtml,
} from "@/lib/articleBody";


export default function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api.get(`/articles/${slug}`)
      .then(({ data }) => {
        setArticle(data);
        if (data.category) {
          return api.get("/articles", { params: { category: data.category, limit: 4 } });
        }
        return { data: [] };
      })
      .then(async (res) => {
        let rel = ((res && res.data) || []).filter((a) => a.slug !== slug);
        if (rel.length < 3) {
          // Top up with recent articles so the section always shows 3 cards.
          const seen = new Set(rel.map((a) => a.slug));
          const fill = await api.get("/articles", { params: { limit: 8 } })
            .then(({ data: d }) => (Array.isArray(d) ? d : []))
            .catch(() => []);
          for (const a of fill) {
            if (rel.length >= 3) break;
            if (a.slug !== slug && !seen.has(a.slug)) { rel.push(a); seen.add(a.slug); }
          }
        }
        setRelated(rel.slice(0, 3));
      })
      .catch((err) => {
        // Merged articles return 404 with a redirect_to target — send readers on.
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.redirect_to) {
          navigate(`/article/${detail.redirect_to}`, { replace: true });
          return;
        }
        setNotFound(true);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const renderBody = () => {
    if (!article) return null;
    const body = sanitizeArticleHtml(article.content || "");
    const isHtml = /<[a-z][\s\S]*>/i.test(body);
    return (
      <div className="sm-article-body" style={BODY_CONTAINER} data-testid="article-body">
        <style>{BODY_SCOPED_CSS}</style>
        {isHtml ? (
          <div dangerouslySetInnerHTML={{ __html: body }} />
        ) : (
          renderPlainTextArticle(body)
        )}
      </div>
    );
  };

  const cover = article?.cover_image_url ? resolveImageUrl(article.cover_image_url) : null;
  const isBookCover = Boolean(article?.category === "Books" && article?.book_cover_url);

  // Word count + first-3-sentence takeaways derived from article content (used for AEO + Key Takeaways card).
  const plainContent = (article?.content || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  const wordCount = plainContent ? plainContent.split(/\s+/).length : 0;
  const takeaways = (() => {
    if (!plainContent) return [];
    if (Array.isArray(article?.key_points) && article.key_points.length) return article.key_points.slice(0, 5);
    // Skip byline/date lead if present (short first block ending with em-dash).
    const withoutLead = plainContent.replace(/^[A-Z][a-z]+ \d{1,2},? \d{4}\s*(—|-)[^.]*\.\s*/, "");
    const sentences = withoutLead.match(/[^.!?]+[.!?]+/g) || [];
    return sentences.slice(0, 3).map((s) => s.trim());
  })();

  const SITE_ORIGIN = "https://summarymapsjournal.com";
  const articleSchemas = article ? [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.meta_description || article.title,
      "image": article.cover_image_url || article.book_cover_url || undefined,
      "datePublished": article.published_at || undefined,
      "dateModified": article.updated_at || article.published_at || undefined,
      "author": {
        "@type": "Person",
        "name": article.author_name || "Travis Moore",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Summary Maps LLC",
        "url": "https://summarymaps.com",
        "logo": { "@type": "ImageObject", "url": "https://summarymaps.com/logo.png" },
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${SITE_ORIGIN}/article/${article.slug}`,
      },
      "wordCount": wordCount || undefined,
      "about": article.category ? { "@type": "Thing", "name": article.category } : undefined,
      "keywords": [article.category, article.book_author, "mind maps", "book summaries"].filter(Boolean).join(", "),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_ORIGIN}/` },
        article.category ? {
          "@type": "ListItem",
          "position": 2,
          "name": article.category,
          "item": `${SITE_ORIGIN}/?category=${encodeURIComponent(article.category)}`,
        } : null,
        { "@type": "ListItem", "position": article.category ? 3 : 2, "name": article.title },
      ].filter(Boolean),
    },
  ] : [];

  return (
    <div className="min-h-screen topo-bg" data-testid="article-page">
      {article ? (
        <SeoHead
          title={article.title}
          description={article.meta_description || article.title}
          image={article.book_cover_url || article.cover_image_url || ""}
          type="article"
          publishedTime={article.published_at}
          category={article.category || ""}
          isArticle
          schemas={articleSchemas}
        />
      ) : !notFound ? (
        // No static <title> exists in index.html anymore — give the tab a sane
        // default while the article fetch is in flight.
        <Helmet>
          <title>SummaryMaps Journal — Mind Maps, Books & Visual Learning</title>
        </Helmet>
      ) : null}
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 pb-20 sm:pb-24">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-brand mb-8 sm:mb-10" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Back to the journal
        </Link>

        {loading ? (
          <div className="space-y-6 animate-pulse">
            <div className="h-4 w-32 bg-ink/10 rounded" />
            <div className="h-12 w-3/4 bg-ink/10 rounded" />
            <div className="aspect-[16/9] bg-ink/10 rounded-2xl" />
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => <div key={i} className="h-4 w-full bg-ink/5 rounded" />)}
            </div>
          </div>
        ) : notFound || !article ? (
          <div className="text-center py-20" data-testid="article-not-found">
            <Helmet>
              <title>Article not found · SummaryMaps Journal</title>
              <meta name="robots" content="noindex" />
            </Helmet>
            <h1 className="font-serif text-3xl mb-4">Article not found</h1>
            <p className="text-ink-muted">It may have been moved or unpublished.</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-6 text-sm font-medium text-brand hover:underline" data-testid="not-found-home-link">
              Back to the journal
            </Link>
          </div>
        ) : (
          <article data-testid="article-content">
            <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-5">
              {article.category ? (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-brand-soft text-brand">
                  {article.category}
                </span>
              ) : null}
              <span className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-ink-subtle">SummaryMaps Journal</span>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-6xl font-semibold leading-[1.1] text-ink">
              {article.title}
            </h1>

            <div
              className="mt-6 sm:mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 border-y border-ink/10 py-3 sm:py-4"
              style={{ color: "#4A5568", fontSize: "0.875rem" }}
              data-testid="article-byline"
            >
              <span>By <span style={{ color: "#1B2F5E", fontWeight: 600 }}>{article.author_name || "Travis Moore"}</span></span>
              <span aria-hidden style={{ color: "#B7B0A0" }}>|</span>
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" style={{ color: "#5C5344" }} /> {formatDate(article.published_at)}</span>
              {article.category ? (
                <>
                  <span aria-hidden style={{ color: "#B7B0A0" }}>|</span>
                  <span data-testid="article-category">{article.category}</span>
                </>
              ) : null}
              {wordCount ? (
                <>
                  <span aria-hidden style={{ color: "#B7B0A0" }}>|</span>
                  <span data-testid="article-wordcount">{wordCount.toLocaleString()} words</span>
                </>
              ) : null}
              {article.category === "Books" && article.book_author ? (
                <>
                  <span aria-hidden style={{ color: "#B7B0A0" }}>|</span>
                  <span data-testid="article-book-author">Book by <span style={{ color: "#1B2F5E", fontWeight: 600 }}>{article.book_author}</span></span>
                </>
              ) : null}
            </div>

            {isBookCover ? (
              <div
                className="relative rounded-2xl sm:rounded-3xl overflow-hidden mt-8 sm:mt-10 flex items-center justify-center px-6 py-10 sm:py-14"
                style={{
                  marginBottom: '2rem',
                  background: 'linear-gradient(135deg, #f6f3ee 0%, #efe7d6 100%)',
                  border: '1px solid rgba(201, 169, 97, 0.25)',
                }}
                data-testid="article-book-cover"
              >
                <img
                  src={article.book_cover_url}
                  alt={`${article.title} — book cover`}
                  width="720"
                  height="960"
                  loading="eager"
                  decoding="async"
                  className="block h-auto w-auto max-h-[420px] sm:max-h-[480px] max-w-full rounded-md"
                  style={{ boxShadow: '0 30px 60px -20px rgba(27, 47, 94, 0.35), 0 12px 24px -12px rgba(27, 47, 94, 0.22)' }}
                />
              </div>
            ) : cover ? (
              <div
                className="relative rounded-2xl sm:rounded-3xl overflow-hidden mt-8 sm:mt-10 aspect-[16/9]"
                style={{ marginBottom: '2rem', background: '#f5f3ef' }}
              >
                <SmartCoverImage src={cover} alt={article.title} width="800" height="450" loading="eager" decoding="async" className="w-full h-full" />
              </div>
            ) : null}

            {article.audio_url ? (
              <div style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                <ArticleAudioPlayer src={article.audio_url} title={article.title} />
              </div>
            ) : null}

            {takeaways.length > 0 ? (
              <div
                data-testid="article-key-takeaways"
                style={{
                  background: '#F5F0E8',
                  border: '2px solid #1B2B4D',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginTop: '2rem',
                  marginBottom: '2rem',
                }}
              >
                <div
                  style={{
                    color: '#1B2B4D',
                    fontWeight: 700,
                    fontSize: '1.05rem',
                    marginBottom: '0.75rem',
                    letterSpacing: '0.01em',
                  }}
                >
                  <span aria-hidden style={{ marginRight: 8 }}>📌</span>Key Takeaways
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '1.25rem',
                    color: '#2D3748',
                    fontSize: '1rem',
                    lineHeight: 1.65,
                    listStyle: 'disc',
                  }}
                >
                  {takeaways.map((t, i) => (
                    <li key={i} style={{ marginBottom: i < takeaways.length - 1 ? '0.4rem' : 0 }}>
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div style={{ marginTop: '2rem' }}>
              {renderBody()}
            </div>

            {article.category === "Books" ? (
              <ReviewSection slug={article.slug} />
            ) : null}

            <AuthorSection
              name={article.author_name}
              bio={article.author_bio}
              avatar={article.author_avatar}
            />

            <ShareSection
              title={article.title}
              url={`https://summarymapsjournal.com/article/${article.slug}`}
              testid="article-share"
            />

            <a
              href="https://summarymaps.com"
              target="_blank"
              rel="noreferrer"
              className="mt-10 sm:mt-12 block group"
              data-testid="article-cta"
            >
              <div
                className="rounded-2xl sm:rounded-3xl px-6 sm:px-10 py-8 sm:py-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 transition-transform hover:-translate-y-0.5"
                style={{
                  background: 'linear-gradient(135deg, #1B2F5E 0%, #14224a 100%)',
                  border: '1px solid rgba(201, 169, 97, 0.3)',
                  boxShadow: '0 20px 50px -20px rgba(27, 47, 94, 0.45)',
                }}
              >
                <div>
                  <div
                    className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] mb-2"
                    style={{ color: '#C9A96E' }}
                  >
                    Try SummaryMaps
                  </div>
                  <div className="font-serif text-xl sm:text-2xl lg:text-3xl text-white leading-tight">
                    Ready to map your next book?
                  </div>
                  <div className="mt-1.5 text-sm sm:text-base text-white/70">
                    Turn any book into a beautiful mind map in ninety seconds.
                  </div>
                </div>
                <span
                  className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 rounded-full text-sm sm:text-base font-medium whitespace-nowrap self-start sm:self-auto transition-transform group-hover:translate-x-0.5"
                  style={{ background: '#C9A96E', color: '#1B2F5E' }}
                >
                  Try SummaryMaps free
                  <span aria-hidden>→</span>
                </span>
              </div>
            </a>

            <div className="mt-14 sm:mt-16">
              <InlineNewsletter />
            </div>
          </article>
        )}

        {related.length > 0 && (
          <div className="mt-20 sm:mt-24 pt-10 sm:pt-12 border-t border-ink/10" data-testid="related-articles">
            <div className="text-xs uppercase tracking-[0.2em] text-brand mb-5 sm:mb-6">You might also like</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
              {related.map((r) => <ArticleCard key={r.slug} article={r} />)}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
